import { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import { tokens } from '../../theme.js'
import {
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  SecondaryButton,
  DangerButton,
  TextField,
  MedBodyText,
  MutedText,
  NativeSelect,
  BoldMedBodyText,
  LinkButton,
  ErrorText,
  PasswordField,
  PasswordToggle
} from '../styles/index.js'
import { errorMessage, errorCode } from '../../api/errors.js'
import { fileCodeCopy } from './errorCopy.js'
import { useExclusionNoteLimit } from '../../hooks/useLimits.js'

const colors = tokens.colors
const fontFamily = tokens.fonts.body

export function NameDialog({ open, title, label, placeholder, helper, initial = '', confirmLabel, confirmTestId, inputTestId, onConfirm, onClose }) {
  const [value, setValue] = useState(initial)
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    if (open) setValue(initial)
  }, [open, initial])
  const name = value.trim()

  async function submit(e) {
    e.preventDefault()
    if (!name || busy) return
    setBusy(true)
    try {
      await onConfirm(name)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <form onSubmit={submit}>
        <DialogTitle>{title}</DialogTitle>
        <MedBodyText style={{ marginBottom: 6 }}>{label}</MedBodyText>
        <TextField
          autoFocus
          fullWidth
          value={value}
          placeholder={placeholder}
          onChange={e => setValue(e.target.value)}
          onFocus={e => e.target.select()}
          inputProps={{ 'data-testid': inputTestId }}
        />
        {helper && <MutedText style={{ marginTop: 10 }}>{helper}</MutedText>}
        <DialogActions>
          <SecondaryButton type="button" onClick={onClose} data-testid="dialog-cancel">
            Cancel
          </SecondaryButton>
          <Button type="submit" disabled={!name} loading={busy} data-testid={confirmTestId}>
            {confirmLabel}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export const DialogPanel = styled.div`
  margin-top: 14px;
  padding: 10px 14px;
  border-radius: 8px;
  background: ${colors.background.light};
  display: flex;
  flex-direction: column;
  gap: 6px;
  font: 400 13px/1.4 ${tokens.fonts.body};
  color: ${colors.text.body};
  & > div {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    min-width: 0;
  }
  & > div > .MuiSvgIcon-root {
    flex: 0 0 auto;
    margin-top: 1px;
  }
  & > div > span {
    overflow-wrap: anywhere;
  }
`

export function ConfirmDeleteDialog({ open, title, children, confirmLabel, confirmIcon, gate, secondaryAction, onConfirm, onClose }) {
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    if (open) setTyped('')
  }, [open])
  const ready = !gate || typed === gate

  async function confirm() {
    if (!ready || busy) return
    setBusy(true)
    try {
      await onConfirm()
    } finally {
      setBusy(false)
    }
  }
  async function secondary() {
    if (busy) return
    setBusy(true)
    try {
      await secondaryAction.onClick()
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} width={460}>
      <DialogTitle data-testid="delete-title">{title}</DialogTitle>
      <div style={{ font: `400 14px/1.5 ${tokens.fonts.body}`, color: colors.text.body }}>{children}</div>
      {gate && (
        <TextField
          autoFocus
          fullWidth
          value={typed}
          placeholder={`Type ${gate} to confirm`}
          onChange={e => setTyped(e.target.value)}
          style={{ marginTop: 16 }}
          inputProps={{ 'data-testid': 'delete-gate' }}
        />
      )}
      <DialogActions style={{ flexWrap: 'wrap' }}>
        {secondaryAction && (
          <LinkButton type="button" onClick={secondary} disabled={busy} style={{ marginRight: 'auto' }} data-testid={secondaryAction.testId}>
            {secondaryAction.label}
          </LinkButton>
        )}
        <SecondaryButton type="button" onClick={onClose} data-testid="dialog-cancel">
          Cancel
        </SecondaryButton>
        <DangerButton type="button" onClick={confirm} disabled={!ready} loading={busy} data-testid="delete-confirm">
          {confirmIcon}
          {confirmLabel}
        </DangerButton>
      </DialogActions>
    </Dialog>
  )
}

export function ApplyResultDialog({ file, onDownloadPackage, downloading, error, onClose }) {
  if (!file) return null
  return (
    <Dialog open onClose={downloading ? undefined : onClose} width={560}>
      <DialogTitle data-testid="apply-result-title">Redactions applied</DialogTitle>
      <MedBodyText>
        The redacted PDF permanently removes the covered content. Export a single package containing that PDF and the summary of every redaction and
        its reason.
      </MedBodyText>
      {error && (
        <p role="alert" style={{ color: colors.error, font: `400 12px/1.4 ${fontFamily}`, margin: '14px 0 0' }} data-testid="apply-download-error">
          {error}
        </p>
      )}
      <DialogActions style={{ flexWrap: 'nowrap' }}>
        <SecondaryButton type="button" onClick={onClose} disabled={downloading} data-testid="apply-result-close">
          Return to files
        </SecondaryButton>
        <Button
          type="button"
          padding="short"
          onClick={onDownloadPackage}
          loading={downloading === 'package'}
          disabled={!!downloading}
          data-testid="export-redaction-package">
          <DownloadOutlinedIcon /> Export redaction package
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export function ExcludeDialog({ files, attachments = 0, onConfirm, onClose }) {
  const noteMax = useExclusionNoteLimit()
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState('')
  const n = files.length
  async function confirm() {
    if (busy) return
    setBusy(true)
    try {
      await onConfirm(note.trim())
    } finally {
      setBusy(false)
    }
  }
  return (
    <Dialog open onClose={onClose} width={460}>
      <DialogTitle data-testid="exclude-title">
        {n === 1 ? 'Exclude this file from the project?' : `Exclude ${n} files from the project?`}
      </DialogTitle>
      <MedBodyText>
        {n === 1 ? 'It leaves' : 'They leave'} the table and Prepare for redaction, and {n === 1 ? 'appears' : 'appear'} under Excluded, where{' '}
        {n === 1 ? 'it' : 'they'} can be restored any time. Nothing is deleted.
        {attachments > 0 && (
          <span data-testid="exclude-attachments-note">
            {` The ${attachments === 1 ? 'attachment' : `${attachments} attachments`} converted from ${n === 1 ? 'the email' : 'the emails'} ${attachments === 1 ? 'is' : 'are'} excluded and restored with ${n === 1 ? 'it' : 'them'}.`}
          </span>
        )}
      </MedBodyText>
      <DialogPanel data-testid="exclude-list">
        {files.map(f => (
          <div key={f.id}>
            <DescriptionOutlinedIcon sx={{ fontSize: 16, color: colors.sectionDivider }} />
            <span>{f.name}</span>
          </div>
        ))}
      </DialogPanel>
      <MedBodyText style={{ margin: '18px 0 6px' }}>Note (optional)</MedBodyText>
      <TextField
        autoFocus
        fullWidth
        value={note}
        placeholder="Leave blank for no note"
        onChange={e => setNote(e.target.value)}
        disabled={busy}
        inputProps={{ 'data-testid': 'exclude-note', maxLength: noteMax, 'aria-label': 'Exclusion note' }}
      />
      <DialogActions>
        <SecondaryButton type="button" onClick={onClose} data-testid="dialog-cancel">
          Cancel
        </SecondaryButton>
        <Button type="button" onClick={confirm} loading={busy} data-testid="exclude-confirm">
          <VisibilityOffOutlinedIcon />
          {n === 1 ? 'Exclude file' : `Exclude ${n} files`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export function MoveToFolderDialog({ title, targets, initial = '', confirmLabel = 'Move', onConfirm, onClose }) {
  const [folderId, setFolderId] = useState(initial)
  const [busy, setBusy] = useState(false)
  async function confirm() {
    if (busy) return
    setBusy(true)
    try {
      await onConfirm(folderId || null)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Dialog open onClose={onClose} width={460}>
      <DialogTitle>{title}</DialogTitle>
      <MedBodyText style={{ marginBottom: 6 }}>Into</MedBodyText>
      <NativeSelect value={folderId} onChange={e => setFolderId(e.target.value)} autoFocus data-testid="move-target-select">
        <option value="">All files (top level)</option>
        {targets.map(g => (
          <option key={g.id} value={g.id}>
            {g.name}
          </option>
        ))}
      </NativeSelect>
      <DialogActions>
        <SecondaryButton type="button" onClick={onClose} data-testid="dialog-cancel">
          Cancel
        </SecondaryButton>
        <Button type="button" onClick={confirm} loading={busy} data-testid="move-confirm">
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export const skipReason = code => fileCodeCopy(code, 'Could not be processed')

const SkippedRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  background: ${colors.errorLight};
  & > .MuiSvgIcon-root {
    font-size: 18px;
    color: ${colors.error};
    flex: 0 0 auto;
    margin-top: 1px;
  }
  & > div {
    min-width: 0;
  }
  & p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

export function BulkResultDialog({ title, skipped, onClose }) {
  return (
    <Dialog open onClose={onClose} width={460}>
      <DialogTitle data-testid="bulk-result-title">{title}</DialogTitle>
      <div data-testid="bulk-result" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <MedBodyText style={{ marginBottom: 4 }}>{skipped.length === 1 ? 'This file was skipped:' : 'These files were skipped:'}</MedBodyText>
        {skipped.map(s => (
          <SkippedRow key={s.id} data-testid="skipped-row" data-file-id={s.id}>
            <ErrorOutlineIcon />
            <div>
              <BoldMedBodyText style={{ font: `500 14px/1.4 ${fontFamily}` }}>{s.name}</BoldMedBodyText>
              <MutedText data-testid="skipped-reason">{s.reason}</MutedText>
            </div>
          </SkippedRow>
        ))}
      </div>
      <DialogActions>
        <Button type="button" onClick={onClose} data-testid="bulk-result-close">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const MIN_PASSWORD = 12
const PASSWORD_FIELDS = [
  ['current', 'Current password', 'current-password', 'current-password'],
  ['next', 'New password', 'new-password', 'new-password'],
  ['confirm', 'Confirm new password', 'confirm-password', 'new-password']
]

export function ChangePasswordDialog({ open, onChange, onClose, required = false }) {
  const [values, setValues] = useState({ current: '', next: '', confirm: '' })
  const [errors, setErrors] = useState({})
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [shown, setShown] = useState({})
  useEffect(() => {
    if (open) {
      setValues({ current: '', next: '', confirm: '' })
      setErrors({})
      setDone(false)
      setShown({})
    }
  }, [open])

  async function submit(e) {
    e.preventDefault()
    if (busy) return
    const found = {}
    if (!values.current) found.current = 'Enter your current password.'
    if (values.next.length < MIN_PASSWORD) found.next = `Use at least ${MIN_PASSWORD} characters.`
    else if (values.confirm !== values.next) found.confirm = "The passwords don't match."
    setErrors(found)
    if (Object.keys(found).length) return
    setBusy(true)
    try {
      await onChange(values.current, values.next)
      setDone(true)
    } catch (err) {
      const code = errorCode(err)
      if (code === 'WRONG_PASSWORD') setErrors({ current: 'The current password is not correct.' })
      else if (code === 'WEAK_PASSWORD') setErrors({ next: errorMessage(err) })
      else if (code === 'RATE_LIMITED') setErrors({ form: 'Too many attempts. Try again in a few minutes.' })
      else setErrors({ form: errorMessage(err) || 'The password could not be changed.' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={busy || required ? undefined : onClose} disableEscapeKeyDown={required} data-testid="change-password-dialog">
      {done ? (
        <>
          <DialogTitle>Password changed</DialogTitle>
          <MedBodyText data-testid="change-password-done">Your other sessions have been signed out. You stay signed in here.</MedBodyText>
          <DialogActions>
            <Button type="button" onClick={onClose} data-testid="change-password-close">
              Close
            </Button>
          </DialogActions>
        </>
      ) : (
        <form onSubmit={submit} noValidate>
          <DialogTitle>{required ? 'Choose your own password' : 'Change password'}</DialogTitle>
          {required && <MedBodyText>Before continuing, replace your temporary password with a password of your own.</MedBodyText>}
          {PASSWORD_FIELDS.map(([key, label, testId, autoComplete], i) => (
            <div key={key} style={{ marginTop: i ? 14 : 0 }}>
              <MedBodyText as="label" htmlFor={`chpw-${key}`} style={{ display: 'block', marginBottom: 6 }}>
                {label}
              </MedBodyText>
              <PasswordField>
                <TextField
                  id={`chpw-${key}`}
                  type={shown[key] ? 'text' : 'password'}
                  autoComplete={autoComplete}
                  autoFocus={i === 0}
                  fullWidth
                  disabled={busy}
                  value={values[key]}
                  error={Boolean(errors[key])}
                  onChange={e => setValues(v => ({ ...v, [key]: e.target.value }))}
                  inputProps={{ 'data-testid': testId }}
                />
                <PasswordToggle
                  shown={shown[key]}
                  onClick={() => setShown(s => ({ ...s, [key]: !s[key] }))}
                  label={`${shown[key] ? 'Hide' : 'Show'} ${label.toLowerCase()}`}
                />
              </PasswordField>
              {errors[key] && (
                <ErrorText role="alert" data-testid={`${testId}-error`}>
                  {errors[key]}
                </ErrorText>
              )}
            </div>
          ))}
          <MutedText style={{ marginTop: 10 }}>At least {MIN_PASSWORD} characters. Changing it signs out your other sessions.</MutedText>
          {errors.form && (
            <ErrorText role="alert" data-testid="change-password-error">
              {errors.form}
            </ErrorText>
          )}
          <DialogActions>
            <SecondaryButton type="button" onClick={onClose} disabled={busy} data-testid="dialog-cancel">
              {required ? 'Sign out' : 'Cancel'}
            </SecondaryButton>
            <Button type="submit" loading={busy} data-testid="change-password-submit">
              Update password
            </Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  )
}
